import { Avatar, Chip, Link, Stack, Typography } from '@mui/material'
import { MenuListItem } from 'utoniq-ui'

import {
  Artist,
  Maybe,
  NotificationBoxFragment,
  OrganizationNotificationBoxFragment,
} from '@/clients/utoniq-core/schema'
import { useBrowserLink } from '@/components/lib/BrowserLink'
import { styleSets } from '@/components/styles'
import { useRouter, useTranslation } from '@/core'
import { Cell } from '@/core/architecture'
import { isExternalLink } from '@/lib/url'

type CampaignBoxProps = {
  notification: NotificationBoxFragment | OrganizationNotificationBoxFragment
  divider?: boolean
  artist?: Maybe<Pick<Artist, 'avatar' | 'displayName'>>
}

export const NotificationBox: Cell<CampaignBoxProps> = ({
  divider,
  notification,
  artist,
}) => {
  const { t } = useTranslation('notifications')
  const { routerPages, generatePath } = useRouter()
  const { onClick } = useBrowserLink(notification.link ?? '')
  const openExternalLink = notification.link
    ? isExternalLink(notification.link)
    : false

  return (
    <MenuListItem
      href={
        notification.link ||
        generatePath(
          isOfficial(notification)
            ? routerPages.OfficialInformationDetail
            : routerPages.NotificationDetail,
          {
            notificationId: notification.id,
          }
        )
      }
      title={
        <>
          {isOfficial(notification) && (
            <Stack direction="row" gap={2} mb={2}>
              {artist?.avatar?.smallUri && (
                <Avatar
                  src={artist?.avatar?.smallUri}
                  sx={{ height: 20, width: 20 }}
                />
              )}
              <Chip
                size="small"
                sx={{
                  height: 20,
                  borderRadius: '2px',
                  bgcolor: 'secondary.dark',
                  py: 1,
                  '.MuiChip-label': {
                    px: 1,
                  },
                }}
                label={
                  <Typography color="white" variant="subtitle2">
                    {t('official')}
                  </Typography>
                }
              />
            </Stack>
          )}
          <Typography variant="h6" sx={styleSets.lineCamp(2)}>
            {notification.title}
          </Typography>
        </>
      }
      subTitle={
        <>
          <Typography
            sx={{ pt: 2, wordBreak: 'break-word', ...styleSets.lineCamp(2) }}
            variant="body2"
          >
            {notification.body}
          </Typography>
          <Typography sx={{ pt: 2 }} variant="overline" color="text.hint">
            {isOfficial(notification)
              ? notification.formattedCreatedAt
              : notification.formattedDate}
          </Typography>
        </>
      }
      sx={{
        height: 'auto',
        gridGap: 0,
        py: 4,
        color: 'text.primary',
      }}
      component={openExternalLink ? 'p' : Link}
      onClick={openExternalLink ? onClick : undefined}
      divider={divider}
    />
  )
}

function isOfficial(
  notification: NotificationBoxFragment | OrganizationNotificationBoxFragment
): notification is OrganizationNotificationBoxFragment {
  return !('date' in notification)
}
